import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Flag, MailCheck, KeyRound } from "lucide-react";
import { api, errMsg } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState({ password: "", confirm: "" });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const inputCls =
    "w-full rounded-lg bg-[#121214] border border-[#3F3F4C] px-4 py-2.5 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-[#FF1E27] transition-colors font-body-sport";

  const requestLink = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (e2) {
      toast.error(errMsg(e2, "Invio fallito"));
    } finally {
      setLoading(false);
    }
  };

  const submitNew = async (e) => {
    e.preventDefault();
    if (pw.password !== pw.confirm) {
      toast.error("Le password non coincidono");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw.password });
      setDone(true);
    } catch (e2) {
      toast.error(errMsg(e2, "Reset fallito"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#121214] flex flex-col">
      <div className="checkered-strip" />
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md rounded-xl border border-[#3F3F4C] bg-[#1A1A1E] p-8" data-testid="reset-password-page">
          <Flag className="w-8 h-8 text-[#FF1E27] mb-4" />
          <h1 className="font-racing font-black text-2xl uppercase text-white leading-tight">
            Recupero <span className="text-[#FF1E27]">Password</span>
          </h1>
          <p className="font-body-sport text-[#FFB703] tracking-[0.3em] text-[10px] mt-2 uppercase mb-8">
            69° Salita dei Monti Iblei
          </p>

          {done && !token && (
            <div className="text-center space-y-4" data-testid="forgot-success">
              <MailCheck className="w-12 h-12 text-[#22C55E] mx-auto" />
              <p className="text-sm text-gray-300 font-body-sport leading-relaxed">
                Se l'email è registrata, riceverai a breve il link per reimpostare la password.
                Controlla anche la cartella Spam. Il link è valido 1 ora.
              </p>
              <Link to="/login" data-testid="back-to-login-link" className="inline-block text-[#FF1E27] hover:underline text-xs font-bold tracking-widest uppercase">
                Torna al Login
              </Link>
            </div>
          )}

          {done && token && (
            <div className="text-center space-y-4" data-testid="reset-success">
              <KeyRound className="w-12 h-12 text-[#22C55E] mx-auto" />
              <p className="text-sm text-gray-300 font-body-sport">
                Password aggiornata con successo! Ora puoi entrare in pista.
              </p>
              <Link to="/login" data-testid="back-to-login-link" className="inline-block rounded-lg bg-[#FF1E27] hover:bg-[#CC1118] transition-colors px-6 py-3 font-racing font-bold text-xs tracking-widest text-white uppercase">
                Vai al Login
              </Link>
            </div>
          )}

          {!done && !token && (
            <form onSubmit={requestLink} className="space-y-4">
              <p className="text-sm text-gray-400 font-body-sport">
                Inserisci l'email del tuo account: ti invieremo un link per reimpostare la password.
              </p>
              <input
                data-testid="forgot-email-input"
                type="email"
                className={inputCls}
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button
                data-testid="forgot-submit-button"
                type="submit"
                disabled={loading}
                className="w-full rounded-lg bg-[#FF1E27] hover:bg-[#CC1118] disabled:opacity-50 transition-colors py-3 font-racing font-bold text-sm tracking-widest text-white uppercase"
              >
                {loading ? "..." : "Invia Link di Recupero"}
              </button>
              <p className="text-center text-[10px] text-gray-600 font-body-sport tracking-widest uppercase">
                <Link to="/login" className="hover:text-[#FFB703] transition-colors">Torna al Login</Link>
              </p>
            </form>
          )}

          {!done && token && (
            <form onSubmit={submitNew} className="space-y-4">
              <p className="text-sm text-gray-400 font-body-sport">
                Scegli la tua nuova password (minimo 6 caratteri).
              </p>
              <input
                data-testid="reset-new-password"
                type="password"
                className={inputCls}
                placeholder="Nuova password"
                value={pw.password}
                onChange={(e) => setPw({ ...pw, password: e.target.value })}
                required
                minLength={6}
              />
              <input
                data-testid="reset-confirm-password"
                type="password"
                className={inputCls}
                placeholder="Conferma nuova password"
                value={pw.confirm}
                onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                required
                minLength={6}
              />
              <button
                data-testid="reset-submit-button"
                type="submit"
                disabled={loading}
                className="w-full rounded-lg bg-[#FF1E27] hover:bg-[#CC1118] disabled:opacity-50 transition-colors py-3 font-racing font-bold text-sm tracking-widest text-white uppercase"
              >
                {loading ? "..." : "Imposta Nuova Password"}
              </button>
            </form>
          )}
        </div>
      </div>
      <div className="checkered-strip" />
    </div>
  );
}
